import { Button } from "@/components/ui/cta-button";
import { ResponsePromiseLine } from "@/components/common/ResponsePromise";

export interface ClosingCtaBandProps {
  id?: string;
  title: string;
  subtitle?: string;
  buttonText: string;
  buttonHref: string;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
}

/**
 * The site-wide closing CTA card — the /hire page's "Let's build your team"
 * card (originally htmls/hire-v3.html): a rounded card inside the page width
 * with an always-dark ink → ember gradient, white headline ending in an
 * orange mark, and a white pill CTA. Same look in light and dark theme.
 * ServiceCtaBand renders this too, so every marketing page ends the same way.
 */
export function ClosingCtaBand({
  id,
  title,
  subtitle,
  buttonText,
  buttonHref,
  secondaryButtonText,
  secondaryButtonHref,
}: ClosingCtaBandProps) {
  // The headline's closing punctuation is set in orange — keep a "?"/"!" the
  // copy already has, otherwise end on an orange period.
  const match = title.trim().match(/^([\s\S]*?)([.?!]*)$/);
  const text = match?.[1] ?? title;
  const mark = match?.[2]?.includes("?") ? "?" : match?.[2]?.includes("!") ? "!" : ".";

  return (
    <section id={id} className="relative z-10 pb-20 md:pb-28">
      <div className="mx-auto max-w-[84rem] px-5 sm:px-8">
        <div
          className="flex flex-col items-center justify-center overflow-hidden rounded-3xl border border-border px-6 py-16 text-center text-white md:py-24"
          style={{
            background:
              "radial-gradient(120% 130% at 50% 115%, color-mix(in oklch, oklch(0.72 0.2 45) 28%, oklch(0.16 0.012 260)) 0%, oklch(0.16 0.012 260) 55%)",
          }}
        >
          <h2 className="mx-auto max-w-3xl text-balance text-4xl font-medium leading-[1.05] tracking-[-0.02em] md:text-6xl">
            {text}
            <span className="text-orange-500">{mark}</span>
          </h2>
          {subtitle && <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/70">{subtitle}</p>}
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button href={buttonHref} variant="light">
              {buttonText}
            </Button>
            {secondaryButtonText && secondaryButtonHref && (
              <Button href={secondaryButtonHref} variant="secondary-dark">
                {secondaryButtonText}
              </Button>
            )}
          </div>
          {/* What the button leads to, so the promise travels with every CTA. */}
          <ResponsePromiseLine tone="onDark" className="mx-auto mt-6 max-w-md" />
        </div>
      </div>
    </section>
  );
}
